// src/components/SmoothScroll.js

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Lenis from 'lenis';

// Generic, performant smooth scrolling via Lenis (the standard smooth-scroll
// library). Unlike the previous custom Framer-transform hijack — which fixed
// the entire app in place and spring-transformed it every frame, causing lag
// and breaking `position: sticky` / parallax — Lenis smoothly drives the real
// native scroll position with a lightweight rAF lerp. Scroll-driven animations
// (useScroll) and sticky elements keep working.
const SmoothScroll = ({ children }) => {
  const { pathname } = useLocation();
  const lenisRef = useRef(null);

  useEffect(() => {
    // Respect users who ask for reduced motion — fall back to native scrolling.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return undefined;
    }

    const lenis = new Lenis({
      duration: 1.05,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      touchMultiplier: 1.5,
    });
    lenisRef.current = lenis;

    let rafId = requestAnimationFrame(function raf(time) {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    });

    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  // Reset scroll position on route change.
  useEffect(() => {
    if (lenisRef.current) {
      lenisRef.current.scrollTo(0, { immediate: true });
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname]);

  return <>{children}</>;
};

export default SmoothScroll;
